import React, { useEffect, useState } from "react";
import LifeRings from "./LifeRings";
import {
  AXIS_META,
  CARD_ORDER,
  SOURCE_EXPLANATION,
  STATE_HELP,
  WINDOW_LABEL,
  cap,
} from "./axisMeta";
import { AxisKey, LifeGPSContext, LifeGPSData } from "./types";

// Spec fallback when the backend sends no indicator.
const MEMORY_FALLBACK = "Personalized with: current timing · recent decisions";

const ENERGY_PHRASE: Record<string, string> = {
  inner: "more inward",
  outer: "more outward",
  balanced: "balanced",
};

type Selection = AxisKey | "phase" | null;

type Props = {
  data: LifeGPSData;
  updating: boolean;
  onAsk: (context: LifeGPSContext, question?: string) => void;
};

// title → phase → summary → rings → legend → cards → next step → why → ask.
const LifeGPSCard: React.FC<Props> = ({ data, updating, onAsk }) => {
  const { currentTheme, interpretation, nextStep, reasoning } = data;
  const axes = data.lifeGps.axes;

  const [selected, setSelected] = useState<Selection>(null);
  const [hovered, setHovered] = useState<AxisKey | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);

  // The ring (and matching card) in focus: hover wins over a pinned selection.
  const active: AxisKey | null =
    hovered ?? (selected && selected !== "phase" ? selected : null);

  // Selection stays until the user taps somewhere else.
  useEffect(() => {
    if (!selected) return;
    const clear = (e: PointerEvent) => {
      if (!(e.target as Element | null)?.closest("[data-lgps-keep]")) {
        setSelected(null);
      }
    };
    document.addEventListener("pointerdown", clear);
    return () => document.removeEventListener("pointerdown", clear);
  }, [selected]);

  // Clicking a ring or legend item highlights its card below; nothing else
  // opens (the hover tooltip is the only popup).
  const toggle = (key: AxisKey | "phase") => {
    if (key === "phase") return;
    const next = selected === key ? null : key;
    setSelected(next);
    if (next) {
      document
        .getElementById(`lgps-card-${next}`)
        ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  };

  const insightFor = (key: AxisKey | "phase") =>
    key === "phase"
      ? { title: currentTheme.label, body: interpretation.oneSentence }
      : {
          title: `${AXIS_META[key].label} · ${cap(axes[key].state)}`,
          body: axes[key].meaning,
        };

  const coreTitle = `${cap(axes.direction.state)} & ${cap(axes.pace.state)}`;
  const coreSub = WINDOW_LABEL[currentTheme.window] ?? "Now";

  const context: LifeGPSContext = {
    theme: currentTheme.label,
    oneSentence: interpretation.oneSentence,
    states: {
      direction: axes.direction.state,
      pace: axes.pace.state,
      energy: axes.energy.state,
      focus: axes.focus.state,
    },
    nextStep: nextStep.action,
  };

  const prompts = [
    `Why is my energy ${ENERGY_PHRASE[axes.energy.state] ?? "this way"} right now?`,
    "What should my one priority be?",
    "When does this phase begin to shift?",
    "How does this affect my relationship life?",
    "What should I avoid during this phase?",
  ];

  return (
    <section className="rounded-3xl border border-slate-100 bg-gradient-to-br from-white via-white to-slate-50 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.45)] p-4 sm:p-6 md:p-8">
      <header className="text-center">
        <h4 className="text-xl sm:text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
          Your Life GPS
        </h4>
        <p className="mt-1 text-sm font-medium text-[#6d4c92]">
          {currentTheme.label}
        </p>
        <p className="one-sentence mx-auto mt-4 mb-7 max-w-[680px] text-base sm:text-lg text-slate-800 leading-snug">
          {interpretation.oneSentence}
        </p>
      </header>

      <details className="mx-auto -mt-3 mb-6 max-w-[680px] rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
        <summary className="cursor-pointer font-semibold text-slate-700">
          What is Life GPS?
        </summary>
        <p className="mt-2">
          Life GPS is a simple map of where your life is right now. It uses your
          birth details, today's timing, and what you have shared with Astria
          (if you allow memory) to describe four things:
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li><b>Direction</b>: where your life is heading.</li>
          <li><b>Pace</b>: how fast things are moving.</li>
          <li><b>Energy</b>: whether your energy points inward or outward.</li>
          <li><b>Focus</b>: what deserves your attention most.</li>
        </ul>
        <p className="mt-2">
          It is guidance to reflect on, not a prediction. You always decide what
          to do. Tap any ring to see what it means.
        </p>
      </details>

      <p className="sr-only">
        Your Life GPS shows {axes.direction.state} direction,{" "}
        {axes.pace.state} pace, {axes.energy.state} energy, and{" "}
        {axes.focus.state} focus.
      </p>

      <div data-lgps-keep className="four-signal-visual">
        <LifeRings
          axes={axes}
          coreTitle={coreTitle}
          coreSub={coreSub}
          active={active}
          coreSelected={selected === "phase"}
          updating={updating}
          tooltipFor={insightFor as (a: AxisKey) => { title: string; body: string }}
          onHover={setHovered}
          onSelect={toggle}
        />

        {/* legend: 2x2 on mobile (label + value), one row on desktop */}
        <div className="mx-auto mt-3 grid max-w-[560px] grid-cols-2 gap-2 lg:flex lg:max-w-none lg:justify-center lg:gap-3">
          {CARD_ORDER.map((axis) => {
            const meta = AXIS_META[axis];
            return (
              <button
                key={axis}
                type="button"
                aria-pressed={selected === axis}
                onClick={() => toggle(axis)}
                className={`flex min-h-[44px] items-center gap-2 rounded-xl border px-3 py-2 text-left transition ${
                  selected === axis
                    ? "border-slate-300 bg-slate-50"
                    : "border-transparent hover:bg-slate-50"
                }`}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: meta.ring }}
                />
                <span className="leading-tight">
                  <span
                    className="block text-xs font-bold"
                    style={{ color: meta.text }}
                  >
                    {meta.label}
                    <span className="font-semibold text-slate-700 lg:hidden">
                      {" "}
                      · {cap(axes[axis].state)}
                    </span>
                  </span>
                  <span className="hidden text-[11px] text-slate-500 lg:block">
                    {meta.helper}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="signal-meanings mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {CARD_ORDER.map((axis) => {
          const meta = AXIS_META[axis];
          const isActive = active === axis;
          return (
            <div
              key={axis}
              id={`lgps-card-${axis}`}
              className="rounded-2xl border bg-white p-4 transition-shadow"
              style={{
                borderColor: isActive ? meta.ring : "#f1f5f9",
                borderTop: `3px solid ${meta.ring}`,
                boxShadow: isActive ? `0 0 0 2px ${meta.ring}55` : undefined,
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <strong className="text-sm" style={{ color: meta.text }}>
                  {meta.label}
                </strong>
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700">
                  {cap(axes[axis].state)}
                </span>
              </div>
              <p className="mt-0.5 hidden text-[11px] text-slate-500 sm:block">
                {meta.helper}
              </p>
              {STATE_HELP[axis][axes[axis].state] && (
                <p className="mt-2 text-xs font-medium" style={{ color: meta.text }}>
                  {cap(axes[axis].state)} means:{" "}
                  {STATE_HELP[axis][axes[axis].state]}
                </p>
              )}
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                {axes[axis].meaning}
              </p>
            </div>
          );
        })}
      </div>

      <div className="next-step mt-5 rounded-2xl bg-slate-900 p-4 sm:p-5 text-white">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">
          Next step
        </span>
        <p className="mt-1 text-sm sm:text-base">{nextStep.action}</p>
        {nextStep.timeHint && (
          <p className="mt-2 text-xs text-slate-300">{nextStep.timeHint}</p>
        )}
      </div>

      <div className="why mt-5 rounded-2xl border border-slate-100 bg-white p-4">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Why Astria thinks this
        </span>
        {reasoning.summary && (
          <p className="mt-1 text-sm text-slate-600">{reasoning.summary}</p>
        )}
        <button
          type="button"
          aria-expanded={sourcesOpen}
          onClick={() => setSourcesOpen((o) => !o)}
          className="memory-indicator mt-3 text-left text-xs text-slate-500 underline decoration-dotted underline-offset-4 hover:text-slate-700"
        >
          {data.memoryIndicator?.render || MEMORY_FALLBACK}
        </button>
        {sourcesOpen && (
          <ul className="mt-2 space-y-1 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
            {reasoning.sources
              .filter((s) => SOURCE_EXPLANATION[s])
              .map((s) => (
                <li key={s}>{SOURCE_EXPLANATION[s]}</li>
              ))}
          </ul>
        )}
      </div>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={() => onAsk(context)}
          className="rounded-full bg-slate-900 px-8 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
        >
          Ask Astria
        </button>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {prompts.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => onAsk(context, q)}
              className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 transition hover:border-[#6d4c92]/40 hover:text-[#6d4c92]"
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LifeGPSCard;
